// formTemplateTypes: {
//       'BANK-WIRE-WITHDRAW': '/PaymentFormDataService/getBankWireWithdrawData',
//       'CREDIT-CARD': '/PaymentFormDataService/getCreditCardData',
//       'EMAIL-CURRENCIES': '/PaymentFormDataService/getEmailCurrenciesData',
//       CLICK2PAY: '/pp?flow=GetClick2PayTemplateData',
//       'ONLY-CURRENCIES': '/PaymentFormDataService/getCurrenciesOnlyData',
//       NETELLER: '/PaymentFormDataService/getNetTellerData',
//       'BANK-WIRE': '/pp?flow=GetBankWireTemplateData'
//     },
//
import { luhnCheck, splitIntoArrOfDigitsBelowTen } from './luhn-check.js';
import { hasCreditcardFormat, hasCreditcardFormatOfCardType } from './creditcard-formats.js';
import { isValidMonthNumber, isYearOf2K, isLaterThanThisMonth } from './creditcard-date.js';
import {
	containsOnlyDigits,
	containsOnlyDigitsOrLetters,
	hasTwoWords,
	// isString,
	// isStringOrNumber,
	hasGreekCurrencyFormat,
	hasStringLengthOf,
	hasStringLengthMinMax,
	isZero,
	removeNonAlphabetOrDigits,
	removeNonDigits,
	removeNonCharsKeepSpace,
	removeNonEmailCharacters,
	limitLength,
	toDigitsAndDecimalPoint,
	ConvertToDecimal
} from './string-num-clean.js';
import isValidEmail from './validate-email.js';
import {
	isValidPhoneNumber,
	isValidCountryCode,
	isValidDKCountryCode,
	isValidDKPhoneNumber
} from './deposit-mobile-number.js';

function validCardnumber(t) {
	const possibleCard = hasCreditcardFormat(t);
	const splitValue = possibleCard ? splitIntoArrOfDigitsBelowTen(t) : null;
	const validCard = splitValue ? luhnCheck(splitValue) : false;
	return validCard;
}

function validUniqueId(regex, id) {
	return id?.match(regex);
}

// Restriction:
// Current usage of inputcleaning is in the payment-method form where
// the input fields for text or number (not the select's) can get
// 'error' even though the output value is ok. This occurs only if
// the 'clean' method allows exactly as many characters as the validation
// prescribes. The solution to this is to set 'clean' to always
// return a stringlenght that is longer than the 'isAccepted' method allows.
const MAX20 = 20;
const MAX30 = 30;
const MAX3 = 3;
const MAX4 = 4;
const MAX16 = 16;
const MAX10 = 10;

const emailCurrencies = {
	em: {
		// email
		isAccepted(val) {
			return isValidEmail(val);
		},
		clean(val) {
			return removeNonEmailCharacters(limitLength(val, 255));
		}
	}
};
const mobileNumber = {
	countryCode: {
		// email
		isAccepted(val) {
			return isValidCountryCode(val);
		},
		clean(val) {
			return removeNonDigits(limitLength(val, 3));
		}
	},
	number: {
		// email
		isAccepted(val) {
			return isValidPhoneNumber(val);
		},
		clean(val) {
			return removeNonDigits(limitLength(val, 13));
		}
	}
};
const mobileNumberForDenmark = {
	countryCode: {
		isAccepted(val) {
			return isValidDKCountryCode(val);
		},
		clean(val) {
			return removeNonDigits(limitLength(val, 3));
		}
	},
	number: {
		isAccepted(val) {
			return isValidDKPhoneNumber(val);
		},
		clean(val) {
			return removeNonDigits(limitLength(val, 10));
		}
	}
};
const amount = {
	withDecimals: {
		isAccepted(val) {
			return hasGreekCurrencyFormat(val);
		},
		clean(val) {
			return toDigitsAndDecimalPoint(val);
		}
	},
	toDecimal(val) {
		return ConvertToDecimal(val);
	}
};
const onlyCurrencies = {
	cu: {
		// currency
		isAccepted(val) {
			return hasStringLengthOf(val, 3);
		}
	}
};
const dotpayBlik = {
	blik: {
		isAccepted(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, 6);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const twoFactAuth = {
	code: {
		isAccepted(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, 6);
		}
	}
};
const neteller = {
	externalid: {
		// accountnumber
		isAccepted(val) {
			return containsOnlyDigitsOrLetters(val) && hasStringLengthMinMax(val, 5, MAX20);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	},
	id: {
		// secureId
		isAccepted(val) {
			return containsOnlyDigitsOrLetters(val) && hasStringLengthMinMax(val, 5, MAX20);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const ecobanq = {
	externalid: {
		// email
		isAccepted(val) {
			return validUniqueId(/[Q]{1}\d{6}/, val);
		},
		clean(val) {
			return removeNonEmailCharacters(limitLength(val, 255));
		}
	},
	pwd: {
		// pwd
		isAccepted(val) {
			return hasStringLengthMinMax(val, 6, MAX20);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const ezeewallet = {
	externalid: {
		// email
		isAccepted(val) {
			return isValidEmail(val);
		},
		clean(val) {
			return removeNonEmailCharacters(limitLength(val, 255));
		}
	},
	pwd: {
		// pwd
		isAccepted(val) {
			return hasStringLengthMinMax(val, 8, MAX30);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const paymeroUpi = {
	externalid: {
		// accountnumber
		isAccepted(val) {
			return validUniqueId(/^\w.+@\w+$/, val);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const akontoPayUpi = {
	externalid: {
		// accountnumber
		isAccepted(val) {
			return validUniqueId(/^\w.+@\w+$/, val);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
const paymeroBanking = {
	bankCode: {
		isAccepted(val) {
			return hasStringLengthMinMax(val, 2, MAX10);
		}
	}
};
const bankWireIndia = {
	transactionId: {
		// email
		isAccepted(val) {
			return containsOnlyDigits(val);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	},
	documentId: {
		isAccepted(val) {
			return hasStringLengthMinMax(val, 8, MAX20 + 4);
		}
	}
};
const ecopayz = {
	accountNumber: {
		// email
		isAccepted(val) {
			return containsOnlyDigits(val) && val >= 1000000000 && val <= 2147483647;
		},
		clean(val) {
			return removeNonDigits(val);
		}
	}
};
const tigerpay = {
	accountNumber: {
		isAccepted(val) {
			return containsOnlyDigits(val) && val >= 100000 && val <= 999999;
		},
		clean(val) {
			return removeNonDigits(val);
		}
	}
};
const swish = {
	phoneNumber: {
		// email
		isAccepted(val) {
			return containsOnlyDigits(val);
		}
	}
};
const creditCard = {
	cn: {
		// cardnumber
		isAccepted(val) {
			return validCardnumber(val);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), MAX16 + 1);
		},
		maxLength(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, MAX16);
		},
		isMasked(val) {
			return val?.includes('xxxx');
		}
	},
	co: {
		// cardowner
		isAccepted(val) {
			return hasTwoWords(val);
		},
		clean(val) {
			// TODO: this method allows multiple spaces in name, but since the
			// name is never requested this clean-method is not used anyhow.
			// const str = isString(val) ? val : val.toString();
			// return limitLength(str.replace(nonCharsButSpace, ''), MAX20);
			return limitLength(removeNonCharsKeepSpace(val), MAX20);
		}
	},
	cc: {
		// cvc
		isAccepted(val) {
			return containsOnlyDigits(val) && hasStringLengthMinMax(val, MAX3, MAX4);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), MAX3 + 1);
		}
	},
	cc4: {
		// cvc
		isAccepted(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, MAX4);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), MAX4 + 1);
		}
	},
	cid: {
		// either cardnumber or card-id must exist to be a valid creditcard deposit
		isAccepted(val) {
			return containsOnlyDigits(val) && hasStringLengthMinMax(val, 1, MAX20) && !isZero(val);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), MAX20 + 1);
		}
	},
	date: {
		// the combined vaidation of year and month
		isAccepted(m, y) {
			return isLaterThanThisMonth(m, y);
		}
	},
	vm: {
		// month
		isAccepted(val) {
			return isValidMonthNumber(val);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), 2);
		},
		maxLength(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, 2);
		},
		padStart(val) {
			// Pad the single digit with a 0
			return val && `0${val}`.slice(-2);
		}
	},
	vy: {
		// year
		isAccepted(val) {
			return isYearOf2K(val);
		},
		clean(val) {
			return limitLength(removeNonDigits(val), 2);
		},
		maxLength(val) {
			return containsOnlyDigits(val) && hasStringLengthOf(val, 2);
		}
	}
};

const venuspoint = {
	externalid: {
		// externalID
		isAccepted(val) {
			return validUniqueId(/^[U]{1}\d{6}$/, val);
		},
		clean(val) {
			return removeNonEmailCharacters(limitLength(val, 255));
		}
	},
	pwd: {
		// pwd
		isAccepted(val) {
			return hasStringLengthMinMax(val, 3, 40);
		},
		clean(val) {
			return limitLength(removeNonAlphabetOrDigits(val), MAX20 + 1);
		}
	}
};
/**
 * Validates if a particular provider has
 * available a particular card type
 *
 * @param {string} methodName
 * @param {string} cardnumber
 * @returns {boolean} boolean
 *
 * */
const validateCardNumberbyProvider = (methodName, cardnumber) => {
	switch (methodName) {
		case 'EMP KwickNGO':
			return hasCreditcardFormatOfCardType(cardnumber, 'Mastercard');
		case 'EMP PassNGO':
			return hasCreditcardFormatOfCardType(cardnumber, 'Visa');

		default:
			return true;
	}
};

const validateCardNumberbyAvailability = (methodName, hasMasterCardMethods, hasVisaMethods, cardnumber) => {
	if (
		methodName === 'EMP CryptoGo' ||
		methodName === 'EMP KwickNGO' ||
		methodName === 'EMP PassNGO' ||
		methodName === 'Kluwp' ||
		methodName === 'Sasono'
	) {
		return true;
	}
	if (hasCreditcardFormatOfCardType(cardnumber, 'Mastercard')) {
		return hasMasterCardMethods;
	}
	if (hasCreditcardFormatOfCardType(cardnumber, 'Visa')) {
		return hasVisaMethods;
	}
	return true;
};

export {
	creditCard,
	emailCurrencies,
	mobileNumber,
	mobileNumberForDenmark,
	neteller,
	ecobanq,
	ecopayz,
	tigerpay,
	ezeewallet,
	onlyCurrencies,
	dotpayBlik,
	amount,
	swish,
	bankWireIndia,
	validateCardNumberbyProvider,
	validateCardNumberbyAvailability,
	twoFactAuth,
	akontoPayUpi,
	paymeroUpi,
	paymeroBanking,
	venuspoint
	// click2pay
};
